import { Directive } from '@angular/core';
import { type ControlValueAccessor } from '@angular/forms';
import { assertIsFunction } from '@pct-ts/assert';
import type {
  ControlValueAccessorOnChange,
  ControlValueAccessorOnTouched,
} from './control-value-access.type';
import { BaseDelegatedFormControlComponent } from './base-delegated-form-control.component';
import { isFunction } from '@pct-ts/typeguard';

/**
 * Makes value accessor implementation easier and delegates the actual form control
 * bound to the component.
 * It can be useful when we need something from the delegated form control that we don't get in the value accessor,
 * for example we want to know if there is a required validator, or we want to monitor the statuses.
 */
@Directive({})
export abstract class AbstractWithDelegatedFormControlComponent<T>
  extends BaseDelegatedFormControlComponent<T>
  implements ControlValueAccessor
{
  #onChange!: ControlValueAccessorOnChange<T>;
  get onChange(): ControlValueAccessorOnChange<T> {
    assertIsFunction(this.#onChange, 'onChange must be injected');
    return this.#onChange;
  }
  #onTouched!: ControlValueAccessorOnTouched;
  get onTouched(): ControlValueAccessorOnTouched {
    assertIsFunction(this.#onTouched, 'onTouched must be injected');
    return this.#onTouched;
  }
  abstract writeValue(value: T): void;

  registerOnChange(fn: ControlValueAccessorOnChange<T>): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: ControlValueAccessorOnTouched): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  /**
   * Use this method to signal value changes when the control value changes
   */
  protected changeValue(value: T): void {
    if (!isFunction(this.#onTouched)) {
      setTimeout(this.changeValue.bind(this, value), 100);
    } else {
      this.onTouched();
      this.onChange(value);
    }
  }
}
